import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { normalizeMaintenanceRequest, normalizeCommunicationNote } from "../utils/enums";
import FullScreenImageViewer, { isImageAttachment } from "./FullScreenImageViewer";
import { ModalShell, Avatar, StatusChip, SuccessState, Shimmer, Field, useBootstrapModal } from "./ModalUI";

// Admin maintenance-request modals (request detail + category manager), shared by the Tenant Requests page.

const API_BASE = process.env.NODE_ENV === "development" ? "/api" : "https://zangon-web-api.azurewebsites.net";
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) { try { return JSON.parse(text); } catch { return null; } }
function getSession() { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } }
function setSession(session) { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getSession()?.refreshToken;
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  const headers = { Accept: "application/json", Authorization: `Bearer ${getSession()?.accessToken || ""}`, ...(options.headers || {}) };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      headers.Authorization = `Bearer ${renewed.accessToken}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      localStorage.removeItem(SESSION_KEY);
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function capitalizeFirst(str) {
  const t = String(str || "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

export const MAINTENANCE_CATEGORIES = [
  { value: "Access Control", label: "Access Control (gate pass, smart locks)", icon: "ki-lock", description: "Gate pass issues, smart lock faults, or access card problems." },
  { value: "Appliance Repair", label: "Appliance Repair (AC, fridge, etc.)", icon: "ki-switch", description: "Repairs for supplied appliances." },
  { value: "Billing / Payment", label: "Billing / Payment Issues", icon: "ki-wallet", description: "Issues with rent or payments." },
  { value: "CCTV / Surveillance", label: "CCTV / Surveillance", icon: "ki-eye", description: "Camera outages, playback requests, or surveillance issues." },
  { value: "Carpentry", label: "Carpentry (doors, wardrobes, cabinets)", icon: "ki-cube-2", description: "Repairs for doors, wardrobes, cabinets, and wooden structures." },
  { value: "Cleaning Services", label: "Cleaning Services (common areas)", icon: "ki-rescue", description: "Cleaning requests for common areas, hallways, or lobbies." },
  { value: "Delivery Issues", label: "Delivery Issues", icon: "ki-truck", description: "Lost or delayed deliveries." },
  { value: "Documentation", label: "Documentation Requests", icon: "ki-file", description: "Requests for official documents." },
  { value: "Drainage / Sewage", label: "Drainage / Sewage", icon: "ki-double-down", description: "Blocked drains, sewage backups, or plumbing emergencies." },
  { value: "Electrical", label: "Electrical", icon: "ki-electricity", description: "Issues with lighting, wiring, switches, or power outlets." },
  { value: "Elevator / Lift", label: "Elevator / Lift", icon: "ki-arrow-up-down", description: "Elevator malfunctions, servicing, or entrapment issues." },
  { value: "Fire Safety", label: "Fire Safety (alarms, extinguishers)", icon: "ki-yii", description: "Fire alarms, smoke detectors, or fire extinguisher checks." },
  { value: "General Inquiry", label: "General Inquiry", icon: "ki-information", description: "General questions or requests." },
  { value: "Generator / Power Supply", label: "Generator / Power Supply", icon: "ki-flash-circle", description: "Generator maintenance, faults, or power changeover issues." },
  { value: "HVAC / Air Conditioning", label: "HVAC / Air Conditioning", icon: "ki-abstract-14", description: "AC servicing, cooling issues, or ventilation repairs." },
  { value: "Internet / Cable Issues", label: "Internet / Cable Issues", icon: "ki-wifi", description: "Wi-Fi outages, slow internet, or cable TV problems." },
  { value: "Landscaping / Gardening", label: "Landscaping / Gardening", icon: "ki-abstract-38", description: "Lawn mowing, tree trimming, or garden maintenance." },
  { value: "Move-in / Move-out", label: "Move-in / Move-out Requests", icon: "ki-exit-right", description: "Move-in/out inspections or approvals." },
  { value: "Neighbor Disputes", label: "Neighbor Disputes", icon: "ki-people", description: "Conflicts between neighbors." },
  { value: "Noise Complaints", label: "Noise Complaints", icon: "ki-call", description: "Complaints regarding excessive noise." },
  { value: "Painting & Finishing", label: "Painting & Finishing", icon: "ki-brush", description: "Repainting walls, touch-ups, or surface finishing." },
  { value: "Parking Issues", label: "Parking Issues", icon: "ki-car", description: "Unauthorized parking or blocked driveways." },
  { value: "Pest Control", label: "Pest Control (rats, termites, mosquitoes)", icon: "ki-abstract-49", description: "Fumigation requests for pests." },
  { value: "Plumbing", label: "Plumbing", icon: "ki-wrench", description: "Issues with pipes, leaks, faucets, or drainage." },
  { value: "Security", label: "Security (guards, incidents, breaches)", icon: "ki-shield", description: "Security guard issues, incidents, or physical breaches." },
  { value: "Service Charge", label: "Service Charge Complaints", icon: "ki-document", description: "Service charge disputes." },
  { value: "Tiling / Flooring", label: "Tiling / Flooring", icon: "ki-grid", description: "Cracked tiles, flooring replacements, or grout repairs." },
  { value: "Visitor Management", label: "Visitor Management", icon: "ki-user", description: "Visitor check-in or access issues." },
  { value: "Waste Disposal", label: "Waste Disposal", icon: "ki-trash", description: "Missed trash pickup, waste bin requests, or hygiene issues." },
  { value: "Water Supply", label: "Water Supply / Borehole Issues", icon: "ki-drop", description: "No water, borehole issues, or water pressure problems." },
];

export const getCategoryIconMeta = (cat) => {
  if (!cat) return { icon: "ki-wrench", bg: "bg-light-primary text-primary" };
  const cLower = cat.toLowerCase();
  const found = MAINTENANCE_CATEGORIES.find(
    (c) => c.value.toLowerCase() === cLower || c.label.toLowerCase().includes(cLower) || cLower.includes(c.value.toLowerCase())
  );
  if (found) {
    let bg = "bg-light-primary text-primary";
    const val = found.value.toLowerCase();
    if (val.includes("plumb") || val.includes("water") || val.includes("drain")) bg = "bg-light-info text-info";
    else if (val.includes("electric") || val.includes("hvac") || val.includes("appliance") || val.includes("power") || val.includes("generator")) bg = "bg-light-warning text-warning";
    else if (val.includes("cleaning") || val.includes("waste") || val.includes("landscaping")) bg = "bg-light-success text-success";
    return { icon: found.icon, bg };
  }
  if (cLower.includes("plumb") || cLower.includes("pipe") || cLower.includes("leak")) return { icon: "ki-wrench", bg: "bg-light-info text-info" };
  if (cLower.includes("water") || cLower.includes("borehole")) return { icon: "ki-drop", bg: "bg-light-info text-info" };
  if (cLower.includes("hvac") || cLower.includes("air") || cLower.includes("ac")) return { icon: "ki-abstract-14", bg: "bg-light-warning text-warning" };
  if (cLower.includes("electric") || cLower.includes("wiring") || cLower.includes("light")) return { icon: "ki-electricity", bg: "bg-light-warning text-warning" };
  if (cLower.includes("sec") || cLower.includes("guard")) return { icon: "ki-shield", bg: "bg-light-primary text-primary" };
  if (cLower.includes("lock") || cLower.includes("gate")) return { icon: "ki-lock", bg: "bg-light-primary text-primary" };
  if (cLower.includes("clean") || cLower.includes("hygiene")) return { icon: "ki-rescue", bg: "bg-light-success text-success" };
  if (cLower.includes("trash") || cLower.includes("waste")) return { icon: "ki-trash", bg: "bg-light-success text-success" };
  return { icon: "ki-wrench", bg: "bg-light-primary text-primary" };
};

function CategoryDropdown({ value, onChange, placeholder = "Select Category..." }) {
  const [searchTerm, setSearchTerm] = useState("");
  const selectedCategory = MAINTENANCE_CATEGORIES.find((c) => c.value === value);
  const selectedIconMeta = getCategoryIconMeta(value || selectedCategory?.value);
  const filteredCategories = MAINTENANCE_CATEGORIES.filter((c) =>
    c.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dropdown w-100">
      <button
        className="form-select form-select-sm text-start d-flex align-items-center justify-content-between w-100 bg-white"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        data-bs-auto-close="outside"
      >
        <span className="d-flex align-items-center text-truncate pe-2">
          {selectedCategory || value ? (
            <>
              <i className={`ki-outline ${selectedIconMeta.icon} fs-5 text-success me-2 flex-shrink-0`}></i>
              <span className="text-gray-800">{selectedCategory ? selectedCategory.label : value}</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
      </button>

      <div className="dropdown-menu w-100 p-3 border-gray-200">
        <div className="position-relative mb-2">
          <i className="ki-outline ki-magnifier fs-6 position-absolute top-50 translate-middle-y ms-3 text-gray-500"></i>
          <input
            type="text"
            className="form-control form-control-sm ps-9 bg-light border-0"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <ul className="list-unstyled mb-0 py-2 mh-250px overflow-y-auto">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <li key={category.value}>
                <button
                  type="button"
                  className={`dropdown-item d-flex align-items-center py-2 px-4 ${value === category.value ? "bg-light-success text-success" : "text-gray-700"}`}
                  onClick={() => {
                    onChange(category.value);
                    setSearchTerm("");
                  }}
                >
                  <i className={`ki-outline ${category.icon} fs-5 me-3 ${value === category.value ? "text-success" : "text-gray-500"}`}></i>
                  <span className="fs-7 fw-semibold">{category.label}</span>
                  {value === category.value && <i className="ki-outline ki-check fs-6 text-success ms-auto"></i>}
                </button>
              </li>
            ))
          ) : (
            <li className="text-center text-muted fs-8 py-3">No categories found</li>
          )}
        </ul>
      </div>
    </div>
  );
}

const PersonSkeleton = () => (
  <div className="d6m-person">
    <Shimmer w={48} h={48} r={999} />
    <div className="flex-grow-1 d-flex flex-column gap-2">
      <Shimmer w="45%" h={14} />
      <Shimmer w="30%" h={10} />
    </div>
    <Shimmer w={72} h={24} r={999} />
  </div>
);

const PRIORITY_DOT = { Low: "#0891B2", Normal: "#a1a1aa", High: "#D97706", Emergency: "#DC2626" };
const REQUEST_STATUS_DOT = { Pending: "#D97706", Completed: "#16A34A", Canceled: "#DC2626" };
const STATUS_TO_INT = { Created: 0, "In Progress": 1, Completed: 2, Canceled: 3 };
const STATUS_SUCCESS_COPY = {
  Completed: { title: "Issue resolved", message: "The request is marked completed and the resident can see it's been handled." },
  Canceled: { title: "Request canceled", message: "The request has been closed without action. The resident can see the update." },
  Pending: { title: "Request reopened", message: "The request is back in the queue and marked as pending." },
};

function normalizeRequestStatus(st) {
  const s = (st || "").toLowerCase();
  if (s.includes("complete") || s.includes("resolve")) return "Completed";
  if (s.includes("cancel") || s.includes("reject")) return "Canceled";
  return "Pending";
}

const RequestDetailSkeleton = () => (
  <>
    <PersonSkeleton />
    <div className="d6m-kv d6m-kv-4 mt-5">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className="d6m-kv-cell">
          <Shimmer w="50%" h={10} />
          <Shimmer w="70%" h={14} className="mt-2" />
        </div>
      ))}
    </div>
    <div className="d6m-section">
      <Shimmer w="30%" h={12} />
      <Shimmer w="100%" h={12} className="mt-3" />
      <Shimmer w="85%" h={12} className="mt-2" />
      <Shimmer w="60%" h={12} className="mt-2" />
    </div>
    <div className="d6m-section">
      <Shimmer w="30%" h={12} />
      <Shimmer w="100%" h={40} r={999} className="mt-3" />
    </div>
  </>
);

export function RequestDetailModal({ modalRef, estateId, request, onClose, onStatusChange }) {
  const [fullRequest, setFullRequest] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("details");
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [success, setSuccess] = useState(null);
  const [fullscreenViewer, setFullscreenViewer] = useState({ isOpen: false, images: [], initialIndex: 0, title: "Maintenance Attachment" });
  const notesEndRef = useRef(null);
  const fetchedIdRef = useRef(null);

  useEffect(() => {
    if (!request?.id || !estateId) return;
    if (fetchedIdRef.current === request.id) return;
    fetchedIdRef.current = request.id;
    setLoading(true);
    setTab("details");
    setFullRequest(request);
    setNotes([]);

    Promise.all([
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}`).then((r) => (r.ok ? r.json() : null)),
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([reqData, notesData]) => {
        const fd = reqData?.data || {};
        let modalBuilding = fd?.buildingNumber || request?.buildingNumber || "Unassigned";
        if (fd?.buildingNumber && fd?.requesterCurrentBuildings) {
          const b = fd.requesterCurrentBuildings.find((x) => x.number === fd.buildingNumber);
          if (b?.phaseName) modalBuilding = `${b.phaseName} - ${fd.buildingNumber}`;
        } else if (request?.buildingNumber?.includes("—")) {
          modalBuilding = request.buildingNumber;
        }
        setFullRequest({
          ...(request || {}),
          ...normalizeMaintenanceRequest(fd),
          title: capitalizeFirst(fd?.title || request?.title || ""),
          description: capitalizeFirst(fd?.description || request?.description || ""),
          tenantTitle: fd?.requesterTitle || request?.requesterTitle || request?.tenantTitle || null,
          tenantName: fd?.requesterName || request?.requesterName || request?.tenantName || "Unknown",
          tenantProfilePictureUrl: fd?.requesterImageUrl || request?.requesterImageUrl || request?.tenantProfilePictureUrl || null,
          buildingNumber: modalBuilding,
        });
        setNotes(parseArrayData(notesData).map(normalizeCommunicationNote));
      })
      .catch(() => toast.error("Failed to load full request details."))
      .finally(() => setLoading(false));
  }, [request, estateId]);

  useEffect(() => {
    if (tab === "thread" && notes.length) notesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [notes, tab]);

  const handleClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    setReplyText("");
    setNotes([]);
    setSuccess(null);
    fetchedIdRef.current = null;
    onClose?.();
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes`,
        { method: "POST", body: JSON.stringify({ message: replyText.trim() }) }
      );
      if (!res.ok) throw new Error("Failed to send reply.");
      setReplyText("");
      const updated = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`
      );
      if (updated.ok) setNotes(parseArrayData(await updated.json()).map(normalizeCommunicationNote));
      setJustSent(true);
      setTimeout(() => setJustSent(false), 1800);
    } catch (err) {
      toast.error(err.message || "Failed to send reply.");
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!newStatus || newStatus === normalizeRequestStatus(fullRequest?.status)) return;
    setUpdatingStatus(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/status`,
        { method: "PATCH", body: JSON.stringify({ status: STATUS_TO_INT[newStatus] ?? 0 }) }
      );
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(safeJsonParse(errText)?.message || "Failed to update status.");
      }
      setFullRequest((prev) => ({ ...prev, status: newStatus }));
      onStatusChange?.(request.id, newStatus);
      setSuccess(STATUS_SUCCESS_COPY[newStatus] || STATUS_SUCCESS_COPY.Pending);
    } catch (err) {
      toast.error(err.message || "Failed to update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const d = fullRequest || request;
  const status = normalizeRequestStatus(d?.status);
  const priority = d?.priorityLevel || d?.priority || "Normal";
  const avatarImg = d ? d.tenantProfilePictureUrl || d.requesterImageUrl || d.tenantAvatar || d.imageUrl : null;
  const residentName = (() => {
    if (!d) return "Resident";
    const tName = d.requesterName || d.tenantName || "";
    const tTitle = d.requesterTitle || d.tenantTitle || d.titlePrefix;
    if (!tName) return "Resident";
    if (tTitle && !tName.toLowerCase().startsWith(tTitle.toLowerCase())) return `${tTitle} ${tName}`;
    return tName;
  })();
  const titleText = (d?.title || d?.maintenanceTitle || d?.shortDescription || "").trim();
  const descText = (d?.description || "").trim();
  const showDesc = descText && descText.toLowerCase() !== titleText.toLowerCase();
  const attachments = Array.isArray(d?.uploadedFileUrls) ? d.uploadedFileUrls : [];
  const imageAttachments = attachments.filter(isImageAttachment);

  return (
    <>
      <ModalShell
        modalRef={modalRef}
        icon="ki-wrench"
        title={titleText || "Maintenance request"}
        onClose={handleClose}
        width={760}
        fill={tab === "thread"}
        overlay={success && <SuccessState title={success.title} message={success.message} onDone={() => setSuccess(null)} />}
        subheader={
          d && (
            <div className="d6m-tabs">
              <button type="button" className={tab === "details" ? "active" : ""} onClick={() => setTab("details")}>Details</button>
              <button type="button" className={tab === "thread" ? "active" : ""} onClick={() => setTab("thread")}>
                Conversation{notes.length > 0 && <span className="d6m-count ms-2">{notes.length}</span>}
              </button>
            </div>
          )
        }
        footer={<button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>}
      >
        {!d ? (
          <div className="d6m-empty">No request selected.</div>
        ) : tab === "details" ? (
          loading ? (
            <RequestDetailSkeleton />
          ) : (
            <>
              <div className="d6m-person">
                <Avatar
                  src={avatarImg}
                  name={residentName}
                  onClick={avatarImg ? () => setFullscreenViewer({ isOpen: true, images: [avatarImg], initialIndex: 0, title: `${residentName} - Profile Photo` }) : undefined}
                />
                <div className="min-w-0 flex-grow-1">
                  <div className="d6m-name text-truncate">{residentName}</div>
                  <div className="d6m-muted text-truncate">{d.tenantPhone || d.phoneNumber || d.phone || d.buildingNumber || "Resident"}</div>
                </div>
                <StatusChip color={REQUEST_STATUS_DOT[status]}>{status}</StatusChip>
              </div>

              <div className="d6m-kv d6m-kv-4 mt-5">
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Created</span>
                  <span className="d6m-v">{fmtDate(d.createdDate || d.meta)}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">{status === "Completed" ? "Completed" : "Last updated"}</span>
                  <span className="d6m-v">{fmtDate(d.lastModified || d.completedDate || d.createdDate)}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Priority</span>
                  <span className="d6m-v d-flex align-items-center gap-2">
                    <span className="d6m-dot" style={{ background: PRIORITY_DOT[priority] || "#a1a1aa" }} />
                    {priority}
                  </span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Category</span>
                  <span className="d6m-v text-truncate">{d.category || "General"}</span>
                </div>
              </div>

              <div className="d6m-section">
                <div className="d6m-section-head"><span>Issue</span></div>
                <div className="d6m-issue-title">{titleText || "No description provided."}</div>
                {showDesc && <p className="d6m-issue-desc">{descText}</p>}
              </div>

              {attachments.length > 0 && (
                <div className="d6m-section">
                  <div className="d6m-section-head">
                    <span>Attachments</span>
                    <span className="d6m-count">{attachments.length}</span>
                  </div>
                  <div className="d6m-attachments">
                    {attachments.map((url, i) =>
                      isImageAttachment(url) ? (
                        <button
                          key={i}
                          type="button"
                          className="d6m-thumb"
                          onClick={() =>
                            setFullscreenViewer({
                              isOpen: true,
                              images: imageAttachments.length > 0 ? imageAttachments : [url],
                              initialIndex: Math.max(0, imageAttachments.indexOf(url)),
                              title: titleText ? `Attachment: ${titleText}` : `Attachment ${i + 1}`,
                            })
                          }
                          title="View full screen"
                        >
                          <img src={url} alt={`Attachment ${i + 1}`} />
                        </button>
                      ) : (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="d6m-file">
                          <i className="ki-outline ki-paper-clip"></i>
                          Attachment {i + 1}
                        </a>
                      )
                    )}
                  </div>
                </div>
              )}

              <div className="d6m-section">
                <div className="d6m-section-head">
                  <span>Update status</span>
                  {updatingStatus && <span className="spinner-border spinner-border-sm text-gray-400"></span>}
                </div>
                <div className="d6m-seg">
                  {["Pending", "Completed", "Canceled"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={status === opt ? "active" : ""}
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(opt)}
                    >
                      <span className="d6m-dot" style={{ background: REQUEST_STATUS_DOT[opt] }} />
                      {opt}
                    </button>
                  ))}
                </div>
                <div className="d6m-hint">Residents see the status change in their app.</div>
              </div>
            </>
          )
        ) : (
          <>
            <div className="d6m-thread">
              {loading ? (
                <div className="d6m-empty">
                  <span className="spinner-border spinner-border-sm me-2 text-gray-400"></span>Loading messages…
                </div>
              ) : notes.length === 0 ? (
                <div className="d6m-empty">
                  <i className="ki-outline ki-messages fs-2x text-gray-300 d-block mb-2"></i>
                  No messages yet. Start the conversation below.
                </div>
              ) : (
                notes.map((note, i) => {
                  const isAdmin = ["admin", "estateadmin"].includes(String(note.senderType || "").toLowerCase());
                  const name = isAdmin ? note.senderName || "Admin" : note.senderName || residentName;
                  const isNewest = justSent && i === notes.length - 1;
                  return (
                    <div key={note.id || i} className={`d6m-msg ${isAdmin ? "mine" : ""} ${isNewest ? "d6-bubble-in" : ""}`}>
                      {!isAdmin && <Avatar src={note.senderAvatar || note.avatarUrl || avatarImg} name={name} size={28} />}
                      <div className="d6m-msg-body">
                        <div className="d6m-msg-meta">
                          <span>{name}</span>
                          <span>{fmtDateTime(note.createdDate)}</span>
                        </div>
                        <div className="d6m-bubble">{note.message}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={notesEndRef} />
            </div>
            <div className="d6m-composer">
              <textarea
                rows={2}
                placeholder="Write a reply to the resident…"
                value={replyText}
                onChange={(e) => {
                  const val = e.target.value;
                  setReplyText(val.length === 1 ? val.toUpperCase() : val);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSendReply();
                }}
              />
              <button
                type="button"
                className={`btn btn-success d6-mbtn flex-shrink-0 ${justSent ? "d6-mbtn-sent" : ""}`}
                onClick={handleSendReply}
                disabled={(!replyText.trim() && !justSent) || sendingReply}
              >
                {sendingReply ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : justSent ? (
                  <>
                    <svg viewBox="0 0 24 24" className="d6-mini-tick me-2" aria-hidden="true"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
                    Sent
                  </>
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </>
        )}
      </ModalShell>
      <FullScreenImageViewer
        isOpen={fullscreenViewer.isOpen}
        images={fullscreenViewer.images}
        initialIndex={fullscreenViewer.initialIndex}
        title={fullscreenViewer.title}
        onClose={() => setFullscreenViewer((prev) => ({ ...prev, isOpen: false }))}
      />
    </>
  );
}

const CategorySkeleton = () => (
  <div className="d6m-list">
    {[1, 2, 3, 4].map((n) => (
      <div key={n} className="d6m-list-row">
        <Shimmer w={40} h={40} r={10} />
        <div className="flex-grow-1 d-flex flex-column gap-2">
          <Shimmer w="40%" h={12} />
          <Shimmer w="65%" h={10} />
        </div>
      </div>
    ))}
  </div>
);

export function ManageCategoriesModal({ modalRef, estateId }) {
  const { hide } = useBootstrapModal(modalRef);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", description: "" });

  const fetchCategories = useCallback(async ({ silent = false } = {}) => {
    if (!estateId) return;
    if (!silent) setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-request-categories`);
      const json = res.ok ? safeJsonParse(await res.text()) : null;
      setCategories(parseArrayData(json));
    } catch {
      toast.error("Failed to load categories");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [estateId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return undefined;
    const onShow = () => {
      if (fetchedEstateId.current === estateId) return;
      fetchedEstateId.current = estateId;
      fetchCategories();
    };
    el.addEventListener("show.bs.modal", onShow);
    return () => el.removeEventListener("show.bs.modal", onShow);
  }, [estateId, modalRef, fetchCategories]);

  const clearForm = () => setForm({ id: "", name: "", description: "" });
  const resetClose = () => { hide(); setSuccess(null); clearForm(); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Category name is required.");
    setSaving(true);
    try {
      const isEdit = !!form.id;
      const url = isEdit
        ? `${API_BASE}/estates/${estateId}/maintenance-request-categories/${form.id}`
        : `${API_BASE}/estates/${estateId}/maintenance-request-categories`;
      const res = await authFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ name: form.name, description: form.description }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to save category.");
      const savedName = form.name;
      clearForm();
      await fetchCategories({ silent: true });
      setSuccess({
        title: isEdit ? "Category updated" : "Category created",
        message: `${savedName} is ${isEdit ? "updated" : "now available"} for residents logging maintenance requests.`,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-category"
      title="Maintenance categories"
      onClose={resetClose}
      overlay={success && <SuccessState title={success.title} message={success.message} duration={2000} onDone={() => setSuccess(null)} />}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={resetClose}>Close</button>}
    >
      <div className="d6m-card">
        <div className="d6m-section-head mb-4">
          <span>{form.id ? `Edit ${form.name}` : "Add a category"}</span>
        </div>
        <div className="d-flex flex-column gap-4">
          <Field label="Category">
            <CategoryDropdown
              value={form.name}
              onChange={(val) => {
                const cat = MAINTENANCE_CATEGORIES.find((c) => c.value === val);
                setForm((p) => ({ ...p, name: val, description: cat?.description || "" }));
              }}
              placeholder="Choose a category…"
            />
          </Field>
          <Field label="Description" hint="Shown to residents when they pick this category.">
            <textarea
              className="d6m-input"
              rows="2"
              placeholder="Brief description…"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Field>
          <div className="d-flex gap-2 justify-content-end">
            {form.id && <button type="button" className="btn btn-light d6-mbtn" onClick={clearForm}>Cancel edit</button>}
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : form.id ? "Save changes" : "Add category"}
            </button>
          </div>
        </div>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head">
          <span>Existing categories</span>
          <span className="d6m-muted">Select one to edit</span>
        </div>
        {loading ? (
          <CategorySkeleton />
        ) : categories.length === 0 ? (
          <div className="d6m-empty">No categories yet. Add your first one above.</div>
        ) : (
          <div className="d6m-list">
            {categories.map((c) => {
              const isEditing = form.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`d6m-list-row ${isEditing ? "active" : ""}`}
                  onClick={() => setForm({ id: c.id, name: c.name, description: c.description || "" })}
                >
                  <span className="d6m-tile"><i className={`ki-outline ${c.icon || getCategoryIconMeta(c.name).icon}`}></i></span>
                  <span className="min-w-0 flex-grow-1 text-start">
                    <span className="d6m-v d-block text-truncate">{c.name}</span>
                    <span className="d6m-muted d-block text-truncate">{c.description || "No description"}</span>
                  </span>
                  <span className="d6m-edit">{isEditing ? "Editing" : "Edit"}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

